// Web Push (service worker + VAPID): сповіщення приходять, навіть коли PWA закрита або заморожена ОС.
// Бекенд дублює push усім SSE-тостам. На iOS (16.4+) push працює лише в PWA, доданій на екран «Додому».
import { isDesktopApp } from './desktopNotify';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3090';
const FLAG_KEY = 'spark.pushActive';

const isIos = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent || '');
const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);

/** 'unsupported' | 'ios-install' (iOS, але не з екрана «Додому») | 'ready' */
export function pushSupport() {
  if (typeof window === 'undefined' || isDesktopApp()) return 'unsupported';
  if ('serviceWorker' in navigator && 'PushManager' in window) return 'ready';
  return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
}

/** Чи підписаний цей пристрій на push (локальна позначка; звіряється з браузером у syncPushState). */
export function isPushActive() {
  try {
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function setFlag(on) {
  try {
    if (on) localStorage.setItem(FLAG_KEY, '1');
    else localStorage.removeItem(FLAG_KEY);
  } catch {
    // приватний режим — позначка лише оптимізація
  }
}

async function api(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

/** Звірити позначку з реальною підпискою браузера; повертає true, якщо пристрій підписаний. */
export async function syncPushState() {
  if (pushSupport() !== 'ready') return false;
  try {
    const active = Boolean(await currentSubscription()) && window.Notification?.permission === 'granted';
    setFlag(active);
    return active;
  } catch {
    return isPushActive();
  }
}

/** Увімкнути push на цьому пристрої (викликати лише з кліку користувача). Кидає Error з текстом для UI. */
export async function enablePush() {
  if (pushSupport() !== 'ready') throw new Error('Push недоступний у цьому браузері');
  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Дозвіл на сповіщення не надано');

  const { enabled, publicKey } = await api('/push/public-key');
  if (!enabled || !publicKey) throw new Error('Push не налаштовано на сервері');

  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  await api('/push/subscribe', { method: 'POST', body: JSON.stringify(sub.toJSON()) });
  setFlag(true);
}

export async function disablePush() {
  try {
    const sub = await currentSubscription();
    if (sub) {
      await api('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => {});
      await sub.unsubscribe();
    }
  } finally {
    setFlag(false);
  }
}

/**
 * Закрити системні push-сповіщення, що ведуть на цей шлях (data.url з sw.js): об'єкт уже відкрито або
 * оброблено в застосунку — копії в шторці лишати не треба. Без підтримки/підписки — тихо нічого не робить.
 */
export async function closePushNotifications(path) {
  if (!path || typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg?.getNotifications) return;
    const list = await reg.getNotifications();
    list.filter((n) => n.data?.url === path).forEach((n) => n.close());
  } catch {
    // best-effort: сповіщення лишиться в шторці
  }
}
